import React, { useCallback, useMemo } from 'react'
import { matchPath } from 'react-router'

import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import LineHeartIcon from 'components/Luxkit/Icons/line/LineHeartIcon'
import SolidHeartIcon from 'components/Luxkit/Icons/solid/SolidHeartIcon'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { createTripSummaryUrl } from 'tripPlanner/utils'

const VIEW_LABEL = 'View'

export default function useBookmarkSnackbarHandlers() {
  const isAlreadyOnTrip = useMemo(
    () =>
      !!matchPath('/trip/:tripId/:tab', {
        exact: false,
      }),
    [],
  )

  const showRemoveSuccessSnackbar = useCallback(
    (tripId: string, tripName: string) => {
      showSnackbar(`Removed from "${tripName}"`, 'neutral', {
        action: !isAlreadyOnTrip
          ? {
              label: VIEW_LABEL,
              to: createTripSummaryUrl({ tripId }),
              onAction: () => {
                Analytics.track(
                  'client-event',
                  clientEvent(
                    'click_remove_success_snackbar',
                    'pressed',
                    'trip_planner',
                    'interaction',
                  ),
                )
              },
            }
          : undefined,
        icon: <LineHeartIcon colour="neutral-one" />,
      })
      Analytics.track(
        'client-event',
        clientEvent(
          'view_remove_success_snackbar',
          'viewed',
          'trip_planner',
          'nonInteraction',
        ),
      )
    },
    [isAlreadyOnTrip],
  )

  const showRemoveErrorSnackbar = useCallback(
    (tripName: string) =>
      showSnackbar(`Failed to remove from "${tripName}"`, 'critical'),
    [],
  )

  const showSaveSuccessSnackbar = useCallback(
    (tripId: string, tripName: string, itemId: string) => {
      showSnackbar(`Saved to "${tripName}"`, 'neutral', {
        action: !isAlreadyOnTrip
          ? {
              label: VIEW_LABEL,
              to: createTripSummaryUrl({ id: itemId, tripId }),
              testId: 'save-success-snackbar-trip-link',
              onAction: () => {
                Analytics.track(
                  'client-event',
                  clientEvent(
                    'click_save_success_snackbar',
                    'pressed',
                    'trip_planner',
                    'interaction',
                  ),
                )
              },
            }
          : undefined,
        icon: <SolidHeartIcon colour="favourite" />,
      })
      Analytics.track(
        'client-event',
        clientEvent(
          'view_save_success_snackbar',
          'viewed',
          'trip_planner',
          'nonInteraction',
        ),
      )
    },
    [isAlreadyOnTrip],
  )

  const showSaveErrorSnackbar = useCallback(
    (tripName: string) =>
      showSnackbar(`Failed to save to "${tripName}"`, 'critical'),
    [],
  )

  return {
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
  }
}
